// src/components/admin/YakViews.tsx
//
// The Yak ops list views, mounted as top-level routes in App.tsx:
//   - YakCustomerHealth /health
//   - YakKbUpdates      /kb-updates
//   - YakProspects      /prospects  (super_admin only)
//
// Story 4.10 removed YakMorningQueue (/queue) and YakEscalations (/escalations)
// along with the pre-pivot resolutions stack they read; the ticket desk
// (Epic 4) is the review surface now.

import { useState } from 'react'
import { Badge } from '../ui/Badge'
import {
  useCustomerHealth,
  useKbUpdates,
  useProspects,
} from '../../hooks/useOpsViews'
import type { YakProspectStage } from '../../types/yak'

function formatDate(value: string | null): string {
  if (!value) return '—'
  const date = new Date(value)
  return (
    date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }) +
    ', ' +
    date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  )
}

function formatDateOnly(value: string | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
}

const tableShellClass =
  'bg-white border border-gray-200 rounded-lg overflow-hidden'
const thClass =
  'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
const tdClass = 'px-6 py-4 whitespace-nowrap text-sm text-gray-900'

function LoadingState({ label }: { label: string }) {
  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="text-center py-4 text-gray-500">{label}</div>
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="text-center py-4 text-red-600">{message}</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Customer Health
// ---------------------------------------------------------------------------
export function YakCustomerHealth() {
  const { health, loading, error } = useCustomerHealth()

  if (loading) return <LoadingState label="Loading customer health..." />
  if (error) return <ErrorState message={error} />

  const formatRate = (rate: number | null) =>
    rate === null ? '—' : `${Math.round(rate * 100)}%`

  const formatAvg = (ms: number | null) => {
    if (ms === null) return '—'
    const minutes = ms / 60000
    if (minutes < 60) return `${Math.round(minutes)} min`
    return `${(minutes / 60).toFixed(1)} hr`
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Customer Health</h2>

      <div className={tableShellClass}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Customer</th>
                <th className={thClass}>Interactions</th>
                <th className={thClass}>Auto-Resolution Rate</th>
                <th className={thClass}>Avg Response Time</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {health.map(h => (
                <tr
                  key={h.organization_id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {h.organization_name}
                  </td>
                  <td className={tdClass}>{h.total}</td>
                  <td className={tdClass}>
                    {formatRate(h.autoResolutionRate)}
                  </td>
                  <td className={tdClass}>{formatAvg(h.avgResponseMs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {health.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No customer data available.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// KB Updates (inbound knowledge-base files awaiting ingestion)
// ---------------------------------------------------------------------------
function kbStatusVariant(status: string): 'green' | 'yellow' | 'blue' | 'gray' {
  if (status === 'complete') return 'green'
  if (status === 'pending') return 'yellow'
  return 'blue'
}

// Strip the "<org-uuid>/<timestamp>-" storage prefix down to the filename.
function kbFileName(filePath: string | null): string {
  if (!filePath) return '—'
  const base = filePath.split('/').pop() ?? filePath
  return base.replace(/^\d{10,}-/, '')
}

export function YakKbUpdates() {
  const { kbUpdates, loading, error, markComplete } = useKbUpdates()
  const [actionError, setActionError] = useState<string | null>(null)
  const [completingId, setCompletingId] = useState<string | null>(null)

  if (loading) return <LoadingState label="Loading KB updates..." />
  if (error) return <ErrorState message={error} />

  const handleComplete = async (id: string) => {
    try {
      setCompletingId(id)
      setActionError(null)
      await markComplete(id)
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to update status'
      )
    } finally {
      setCompletingId(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">KB Updates</h2>
        <div className="text-sm text-gray-600">
          {kbUpdates.filter(u => u.status !== 'complete').length} pending
        </div>
      </div>

      {actionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
          {actionError}
        </div>
      )}

      <div className={tableShellClass}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>File</th>
                <th className={thClass}>Sender</th>
                <th className={thClass}>Notes</th>
                <th className={thClass}>Status</th>
                <th className={thClass}>Received</th>
                <th className={thClass}>Completed</th>
                <th className={thClass}>Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {kbUpdates.map(u => (
                <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                    {kbFileName(u.file_path)}
                  </td>
                  <td className={tdClass}>{u.sender_email || '—'}</td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                    {u.notes || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant={kbStatusVariant(u.status)}>
                      {u.status.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(u.received_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(u.completed_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {u.status !== 'complete' && (
                      <button
                        onClick={() => handleComplete(u.id)}
                        disabled={completingId === u.id}
                        className="text-brand-600 hover:text-brand-800 font-medium disabled:opacity-50"
                      >
                        {completingId === u.id ? 'Saving...' : 'Mark complete'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {kbUpdates.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No KB updates received yet. Files emailed to the updates address
              will appear here.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Prospects (super_admin only — gated by SuperAdminRoute in App.tsx)
// ---------------------------------------------------------------------------
function prospectStageVariant(
  stage: YakProspectStage
): 'green' | 'yellow' | 'red' | 'blue' | 'gray' {
  switch (stage) {
    case 'won':
      return 'green'
    case 'pilot':
      return 'blue'
    case 'demo_completed':
      return 'yellow'
    case 'lost':
      return 'red'
    default:
      return 'gray'
  }
}

export function YakProspects() {
  const { prospects, loading, error } = useProspects()

  if (loading) return <LoadingState label="Loading prospects..." />
  if (error) return <ErrorState message={error} />

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Prospects</h2>
        <div className="text-sm text-gray-600">
          {prospects.length} in pipeline
        </div>
      </div>

      <div className={tableShellClass}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Company</th>
                <th className={thClass}>Contact</th>
                <th className={thClass}>Product</th>
                <th className={thClass}>Reps</th>
                <th className={thClass}>Stage</th>
                <th className={thClass}>Next Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {prospects.map(p => (
                <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {p.company}
                  </td>
                  <td className={tdClass}>
                    {p.contact_name || p.email || '—'}
                  </td>
                  <td className={tdClass}>{p.product || '—'}</td>
                  <td className={tdClass}>{p.rep_count ?? '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant={prospectStageVariant(p.stage)}>
                      {p.stage.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDateOnly(p.next_action_date)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {prospects.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No prospects in the pipeline.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
